import { LoaderCircle, X } from 'lucide-react'
import { useState } from 'react'

import { useEmpresa } from '../../context/EmpresaContext'
import { supabase } from '../../lib/supabase'
import { normalizarRut, validarRut } from '../../utils/rut'

function Campo({ label, requerido = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

function ClienteModal({ cliente, onClose, onGuardado }) {
  const { empresaActual } = useEmpresa()
  const esEdicion = Boolean(cliente)

  const [razonSocial, setRazonSocial] = useState(
    cliente?.razon_social || ''
  )
  const [rut, setRut] = useState(cliente?.rut || '')
  const [correo, setCorreo] = useState(cliente?.correo || '')
  const [telefono, setTelefono] = useState(
    cliente?.telefono || ''
  )
  const [contacto, setContacto] = useState(
    cliente?.contacto || ''
  )
  const [giro, setGiro] = useState(cliente?.giro || '')
  const [direccion, setDireccion] = useState(
    cliente?.direccion || ''
  )
  const [comuna, setComuna] = useState(cliente?.comuna || '')
  const [ciudad, setCiudad] = useState(cliente?.ciudad || '')
  const [activo, setActivo] = useState(cliente?.activo ?? true)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')

  const cerrar = () => {
    if (guardando) return
    onClose()
  }

  const guardarCliente = async (event) => {
    event.preventDefault()

    if (guardando) return

    setError('')

    const nombreLimpio = razonSocial.trim()

    if (!nombreLimpio) {
      setError('Debes ingresar el nombre o razón social.')
      return
    }

    if (!validarRut(rut)) {
      setError('El RUT ingresado no es válido.')
      return
    }

    const rutNormalizado = normalizarRut(rut)

    const datosCliente = {
      razon_social: nombreLimpio,
      rut: rutNormalizado || null,
      giro: giro.trim() || null,
      direccion: direccion.trim() || null,
      comuna: comuna.trim() || null,
      ciudad: ciudad.trim() || null,
      telefono: telefono.trim() || null,
      correo: correo.trim() || null,
      contacto: contacto.trim() || null,
    }

    try {
      setGuardando(true)

      let clienteGuardado = null

      if (esEdicion) {
        const { data, error: errorActualizar } = await supabase
          .from('clientes')
          .update({ ...datosCliente, activo })
          .eq('id', cliente.id)
          .eq('empresa_id', empresaActual.id)
          .select()
          .single()

        if (errorActualizar) {
          throw errorActualizar
        }

        clienteGuardado = data
      } else {
        const { data, error: errorInsertar } = await supabase
          .from('clientes')
          .insert({
            empresa_id: empresaActual.id,
            ...datosCliente,
            activo: true,
          })
          .select()
          .single()

        if (errorInsertar) {
          throw errorInsertar
        }

        clienteGuardado = data
      }

      onGuardado?.(clienteGuardado)
    } catch (errorGuardado) {
      console.error('Error al guardar cliente:', errorGuardado)

      if (errorGuardado.code === '23505') {
        setError(
          'Ya existe un cliente con ese RUT en esta empresa.'
        )
      } else {
        setError(
          errorGuardado.message ||
            'No fue posible guardar el cliente.'
        )
      }
    } finally {
      setGuardando(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          cerrar()
        }
      }}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-xl sm:p-8">
        <div className="mb-6 flex items-start justify-between">
          <h2 className="text-xl font-bold text-slate-900">
            {esEdicion ? 'Editar cliente' : 'Nuevo cliente'}
          </h2>

          <button
            type="button"
            onClick={cerrar}
            disabled={guardando}
            aria-label="Cerrar"
            className="text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed"
          >
            <X size={22} />
          </button>
        </div>

        <form
          className="max-h-[75vh] space-y-6 overflow-y-auto pr-1"
          onSubmit={guardarCliente}
        >
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Datos principales
            </h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Campo label="Nombre / Razón social" requerido>
                  <input
                    type="text"
                    value={razonSocial}
                    onChange={(event) =>
                      setRazonSocial(event.target.value)
                    }
                    disabled={guardando}
                    className={estiloInput}
                  />
                </Campo>
              </div>

              <Campo label="RUT">
                <input
                  type="text"
                  value={rut}
                  onChange={(event) => setRut(event.target.value)}
                  placeholder="12.345.678-9"
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>

              <Campo label="Giro">
                <input
                  type="text"
                  value={giro}
                  onChange={(event) =>
                    setGiro(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Contacto
            </h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Campo label="Correo">
                <input
                  type="email"
                  value={correo}
                  onChange={(event) =>
                    setCorreo(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>

              <Campo label="Teléfono">
                <input
                  type="text"
                  value={telefono}
                  onChange={(event) =>
                    setTelefono(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>

              <div className="sm:col-span-2">
                <Campo label="Persona de contacto">
                  <input
                    type="text"
                    value={contacto}
                    onChange={(event) =>
                      setContacto(event.target.value)
                    }
                    disabled={guardando}
                    className={estiloInput}
                  />
                </Campo>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-6">
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
              Dirección
            </h3>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Campo label="Dirección">
                  <input
                    type="text"
                    value={direccion}
                    onChange={(event) =>
                      setDireccion(event.target.value)
                    }
                    disabled={guardando}
                    className={estiloInput}
                  />
                </Campo>
              </div>

              <Campo label="Comuna">
                <input
                  type="text"
                  value={comuna}
                  onChange={(event) =>
                    setComuna(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>

              <Campo label="Ciudad">
                <input
                  type="text"
                  value={ciudad}
                  onChange={(event) =>
                    setCiudad(event.target.value)
                  }
                  disabled={guardando}
                  className={estiloInput}
                />
              </Campo>
            </div>
          </div>

          {esEdicion && (
            <label className="flex items-center gap-3 text-sm font-semibold text-slate-700">
              <input
                type="checkbox"
                checked={activo}
                onChange={(event) =>
                  setActivo(event.target.checked)
                }
                disabled={guardando}
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
              />
              Cliente activo
            </label>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-2.5 font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando && (
                <LoaderCircle
                  size={18}
                  className="animate-spin"
                />
              )}

              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100'

export default ClienteModal
